import { Badge, Button } from '@design-system';
import { ContactLtcSpecialistButton } from '@/components/GroupInsurancePlanSelector';
import { LtcFundingSlideOutContent } from '@/components/LearnMoreSlideOutContent';
import { MethodologyCallout } from '@/shared/components/methodology-callout';
import { SlideContent } from '@/components/SlideContent';
import { agisEnrollmentUrl } from '@/components/GroupInsurancePlanSelector';
import { Citations, citations } from '@/features/client-onboarding/components/citations';
import { AdvisorCustomDisclosures } from '@/features/client-onboarding/components/advisor-custom-disclosures';
import {
  OnboardingLayout,
  useSlideSetup,
  useFundingCoverageSlide,
} from '@/features/client-onboarding';
import { OnboardingDownloadReport } from '@/pages/OnboardingDownloadReport';
import { OnboardingFundingSources } from '@/pages/OnboardingFundingSources';
import { OnboardingCarePhasesSummary } from '@/pages/OnboardingCarePhasesSummary';
import { selectClient, selectInferenceSet, selectSession, selectFeatureFlags } from '@/model';
import { cn } from '@/lib/utils';

import { formatPercent, formatThousands } from '@shared';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { SessionType } from '@shared';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { InfoIcon } from 'lucide-react';

export function OnboardingFundingCoverage({ clientId }: ClientIdProps) {
  const {
    featureFlags,
    groupPolicyFeatureFlag,
    combinedProjectedCostCoverage,
    combinedProjectedCostCoveragePercent,
    beginningText,
    showNoCoverageSelectedText,
    hasSelfFunding,
    previousSlide,
  } = useFundingCoverageSlide();

  const { sessionType } = useSelector(selectSession);
  const featureFlagsRedux = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlagsRedux.d2cMode;
  const navigate = useNavigate();

  const handleD2CBack = () => {
    navigate(`/clients/${clientId}/onboarding/guided-planning/plan-comparison`);
  };

  useSlideSetup({
    slidePage: OnboardingFundingCoverage,
    nextSlidePage: OnboardingDownloadReport,
    previousSlidePage: isD2C ? undefined : (previousSlide ?? OnboardingFundingSources),
    onBackClick: isD2C ? handleD2CBack : undefined,
    nextButtonLabel: 'Next',
  });

  return (
    <OnboardingLayout>
      <SlideContent
        headerText={''}
        data-testid="coverage-header"
      >
        {/* Hide for VB Shop if Zero coverage */}
        {!showNoCoverageSelectedText && (
          <>
            <FundingCoverageText
              beginningText={beginningText}
              combinedProjectedCostCoverage={combinedProjectedCostCoverage}
              combinedProjectedCostCoveragePercent={combinedProjectedCostCoveragePercent}
            />
            <HowDidWeCalculateThis />
          </>
        )}

        {showNoCoverageSelectedText && (
          <div className="flex flex-col items-center justify-center gap-4">
            <NoCoverageSelectedText className="mb-4 mt-8" />

            <ContactLtcSpecialistButton
              featureFlags={featureFlags}
              buttonClassName="w-full p-6 md:w-auto"
              iconClassName="h-6 w-6"
              children="Talk to an LTC Specialist"
            />
          </div>
        )}

        {groupPolicyFeatureFlag && !showNoCoverageSelectedText && (
          <div className="my-8 flex flex-col items-center justify-center gap-2 md:flex-row">
            <Button className="w-full p-6 md:w-auto">
              {featureFlags.agis ? (
                <a
                  href={agisEnrollmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Enroll Now
                </a>
              ) : (
                'Enroll Now'
              )}
            </Button>

            <ContactLtcSpecialistButton
              featureFlags={featureFlags}
              buttonClassName="w-full p-6 md:w-auto"
              iconClassName="h-6 w-6"
              children="Talk to an LTC Specialist"
              variant="secondary"
            />
          </div>
        )}
        <div className="mt-auto pt-4">
          <Citations
            className="w-full"
            citations={hasSelfFunding ? [citations.OnboardingFundingCoverageDisclaimer] : undefined}
          />
          <AdvisorCustomDisclosures className="w-full" />
        </div>
      </SlideContent>
    </OnboardingLayout>
  );
}

type FundingCoverageTextProps = {
  beginningText: string;
  combinedProjectedCostCoverage: number | undefined;
  combinedProjectedCostCoveragePercent: number | undefined;
};

function HowDidWeCalculateThis() {
  return (
    <div
      className="mt-8 flex justify-center"
      data-testid="coverage-faq-link"
    >
      <MethodologyCallout
        content={<LtcFundingSlideOutContent />}
        title="FAQ"
        variant="content"
      >
        <Badge className="md:text-base md:py-1 md:pl-2! md:pr-3! hover:bg-primary hover:border-primary-dark hover:text-primary-foreground transition-all duration-200">
          <InfoIcon className='md:size-5!' />How did we calculate this?
        </Badge>
      </MethodologyCallout>
    </div>
  );
}

function FundingCoverageText({
  beginningText,
  combinedProjectedCostCoverage,
  combinedProjectedCostCoveragePercent,
}: FundingCoverageTextProps) {
  return (
    <div className="mt-8 flex h-full w-full flex-col items-center justify-center text-center md:mt-16">
      <h1
        className="text-2xl font-semibold md:text-4xl"
        data-testid="coverage-text"
      >
        {beginningText} is projected to cover{' '}
        <span
          className="text-primary"
          data-testid="coverage-amount"
        >
          <LtcCostDisplay value={combinedProjectedCostCoverage ?? 0} />
        </span>
        , or{' '}
        <span
          className="text-primary"
          data-testid="coverage-percent"
        >
          {combinedProjectedCostCoverage
            ? formatPercent(combinedProjectedCostCoveragePercent, 0)
            : '0%'}{' '}
        </span>
        of your total predicted LTC costs! 🎉
      </h1>
    </div>
  );
}

function NoCoverageSelectedText({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex h-full w-full flex-col items-center justify-center gap-4 text-center md:gap-2',
        className,
      )}
    >
      <h1 className="flex items-center text-2xl font-semibold md:text-3xl">
        Did you mean to waive your long-term care benefits?
      </h1>
      <LtcProjections className="mt-4" />
      <p className="mt-4 text-lg md:text-xl">
        We can help you understand your options and potentially{' '}
        <span className="font-semibold">lower your future costs.</span>
      </p>
    </div>
  );
}

function LtcProjections({ className }: { className?: string }) {
  const { ltcLikelihoodEver } = useSelector(selectInferenceSet);
  const {
    appliedInferenceSet: { ltcAtAge },
  } = useSelector(selectClient);
  const {
    intakeSurvey: { clientHasStartedLtc },
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost, allPhaseCareMonthsNeeded },
  } = useSelector(selectClient);

  // Base projections that are always shown
  const baseProjections = [
    {
      name: 'LTC Costs',
      isLtcCost: true as const,
      value: allPhaseInflatedProfessionalShareCost,
    },
    {
      name: clientHasStartedLtc ? 'Remaining Duration (Years)' : 'Duration (Years)',
      isLtcCost: false as const,
      value: formatThousands(allPhaseCareMonthsNeeded / 12, '0', 1),
    },
  ];

  // Additional projections for clients who haven't started LTC
  const additionalProjections = clientHasStartedLtc
    ? []
    : [
        {
          name: 'Likelihood of LTC',
          isLtcCost: false as const,
          value: formatPercent(ltcLikelihoodEver, 0),
        },
        {
          name: 'Start Age',
          isLtcCost: false as const,
          value: ltcAtAge,
          unit: 'years old',
        },
      ];

  const projections = [...baseProjections, ...additionalProjections];
  return (
    <div className={cn('mx-auto max-w-7xl px-6 lg:px-8', className)}>
      <div className="mx-auto max-w-2xl lg:max-w-none">
        <div className="mt-2">
          <h2 className="font-medium">Your Long-Term Care Projections</h2>
        </div>
        <dl
          className={`mt-4 grid grid-cols-1 gap-0.5 overflow-hidden rounded-2xl text-center sm:grid-cols-2 lg:grid-cols-${projections.length}`}
        >
          {projections.map(stat => (
            <div
              key={stat.name}
              className="flex flex-col bg-gray-400/5 p-8"
            >
              <dt className="text-muted-foreground text-sm font-semibold leading-6">{stat.name}</dt>
              <dd className="text-foreground order-first text-3xl font-semibold tracking-tight">
                {stat.isLtcCost ? <LtcCostDisplay value={stat.value as number} /> : stat.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
